/**
 * 근거 클릭 시 보여줄 원천 데이터 (목).
 *
 * 설계 원칙 — 대표님은 계산하러 오신 게 아니라 판단하러 오셨다.
 *
 *   1) 결론 먼저.  "왜 이 사람이 올라왔는가"를 근거 2~3개로 쪼개서 번호를 붙인다.
 *   2) 숫자 옆에는 항상 비교 대상.  46%가 좋은지 나쁜지는 혼자서는 알 수 없다.
 *      "이 회원 6회 / 이탈한 회원 7회 / 재등록한 회원 11회"처럼 나란히 놓으면
 *      설명 없이도 눈으로 판정된다.
 *   3) 계산식과 용어 정의는 접어둔다.  의심이 들 때만 펴보면 된다.
 *   4) 근거의 성격을 구분한다.  행동 신호(SIGNAL)와 구조적 위험(SEGMENT)과
 *      시급성(TIMING)은 서로 다른 종류의 이유인데 같이 빨갛게 칠하면 뭐가 진짜
 *      이유인지 사라진다.
 */

/** 지표 신뢰도 — 4~6차 검증 결과 반영 */
export type Confidence = 'VERIFIED' | 'WEAK' | 'REFERENCE';

export const CONFIDENCE_LABEL: Record<Confidence, { text: string; desc: string; color: string; bg: string }> = {
    VERIFIED: {
        text: '검증됨',
        desc: '실제 이탈 데이터로 예측력이 확인된 지표입니다',
        color: '#00795F',
        bg: '#E3F1EC',
    },
    WEAK: {
        text: '약한 신호',
        desc: '예측력은 있으나 크지 않습니다. 단독으로 판단하지 않습니다',
        color: '#B45309',
        bg: '#FEF3C7',
    },
    REFERENCE: {
        text: '참고용',
        desc: '검증에서 예측력이 확인되지 않았습니다. 이해를 돕는 용도로만 표시합니다',
        color: '#7C8794',
        bg: '#F1F4F9',
    },
};

/**
 * 근거의 성격.
 * 셋을 구분해야 "지금 뭘 해야 하는지"가 달라진다.
 *   SIGNAL  — 이 회원이 실제로 보인 행동 변화. 판단의 핵심.
 *   SEGMENT — 이 회원이 속한 구간의 구조적 위험. 개인 탓이 아니다.
 *   TIMING  — 남은 시간. 판단이 아니라 마감이다.
 *   CONTEXT — 참고. 판단 근거로 쓰지 않는다.
 */
export type ReasonKind = 'SIGNAL' | 'SEGMENT' | 'TIMING' | 'CONTEXT';

export const REASON_KIND_META: Record<ReasonKind, { label: string; desc: string; color: string; bg: string }> = {
    SIGNAL: {
        label: '행동 변화',
        desc: '이 회원이 실제로 보인 변화입니다. 판단의 핵심 근거입니다',
        color: '#C22D38',
        bg: '#FFF0F0',
    },
    SEGMENT: {
        label: '구간 위험',
        desc: '이 회원이 속한 구간 자체가 원래 위험합니다. 개인의 문제가 아닙니다',
        color: '#B45309',
        bg: '#FEF3C7',
    },
    TIMING: {
        label: '남은 시간',
        desc: '판단이 아니라 마감입니다. 언제까지 움직여야 하는지를 뜻합니다',
        color: '#2C4C8C',
        bg: '#EDF2FA',
    },
    CONTEXT: {
        label: '참고',
        desc: '판단 근거로 쓰지 않았습니다. 상황을 이해하는 데만 도움이 됩니다',
        color: '#7C8794',
        bg: '#F1F4F9',
    },
};

/** 막대 비교 — 내 회원 하나와 기준선들을 나란히 */
export type CompareBar = {
    /** 단위. '회', '%', '시간' 등 */
    unit: string;
    /** 이 회원(강사)의 값 */
    mine: { label: string; value: number };
    /** 비교 기준들. tone 으로 좋은 쪽/나쁜 쪽을 구분 */
    others: { label: string; value: number; tone?: 'BAD' | 'GOOD' }[];
};

/** 카운트다운 — 시급성은 막대가 아니라 숫자판이 맞다 */
export type CountdownItem = { label: string; value: string; caption?: string; alert?: boolean };

export type Reason = {
    kind: ReasonKind;
    /** 근거 제목 — 그 자체로 문장이 되어야 한다 */
    title: string;
    /** 숫자를 읽는 법. 쉬운 말로 */
    plain: string;
    confidence?: Confidence;
    compare?: CompareBar;
    countdown?: CountdownItem[];
};

/** 수업 간격 타임라인 — 표로는 안 보이는 "벌어짐"을 눈으로 */
export type LessonDot = { date: string; status: '출석' | '노쇼' | '취소' };
export type Timeline = {
    title: string;
    /** 축 시작·끝 (YYYY-MM-DD) */
    from: string;
    to: string;
    dots: LessonDot[];
    /** 무엇을 보라는 안내 */
    note: string;
};

export type RawRow = { cells: string[]; alert?: boolean; muted?: boolean };

export type RawSection = {
    label: string;
    /** 이 섹션 지표의 신뢰도 */
    confidence?: Confidence;
    /** 계산식 설명 */
    formula?: string;
    /** 비교 기준 */
    benchmark?: string;
    columns: string[];
    rows: RawRow[];
    note?: string;
};

export type RawData = {
    title: string;
    subtitle: string;
    /** 판단 요약 — 한 줄 결론 */
    verdict: { headline: string; body: string; grade: 'DANGER' | 'WARNING' | 'CHANCE' | 'PRAISE' };
    /** 왜 올라왔는가 — 번호가 붙는 근거들 */
    reasons: Reason[];
    /** 수업 간격 시각화 (있는 경우) */
    timeline?: Timeline;
    /** 상단 요약 지표 */
    highlights: { label: string; value: string; sub?: string; alert?: boolean }[];
    /** 확인한 원천 기록 — 기본은 접혀 있다 */
    sections: RawSection[];
    /** 용어 사전 — 접힘 */
    glossary?: { term: string; desc: string }[];
};

const PACE_GLOSSARY = [
    {
        term: '유효수업',
        desc: '실제로 진행된 수업만 셉니다. 취소된 수업은 빼고, 출석과 노쇼는 포함합니다. (노쇼도 회차가 차감되기 때문입니다)',
    },
    {
        term: '소진 페이스',
        desc: '최근 두 달 수업 횟수를 그 앞 두 달과 비교한 값입니다. 100%면 예전과 똑같은 속도, 50%면 절반으로 줄었다는 뜻입니다.',
    },
    {
        term: '잔여 회차',
        desc: '앞으로 받을 수 있는 수업 횟수입니다. 이미 예약해둔 미래 수업도 빠진 숫자라, 실제로 새로 잡을 수 있는 횟수를 뜻합니다.',
    },
    {
        term: '수강권 순번',
        desc: '이 회원이 몇 번째 수강권을 쓰고 있는지입니다. 첫 번째 회원의 이탈률이 가장 높고, 순번이 올라갈수록 크게 떨어집니다.',
    },
];

export const mockRawData: Record<string, RawData> = {
    bi_1: {
        title: '이하늘 회원',
        subtitle: '담당 김한솔 강사 · PT 20회권 · 2026-03-14 등록 · 첫 수강권',
        verdict: {
            grade: 'DANGER',
            headline: '첫 수강권이 끝나가는데 수업 속도가 절반으로 떨어졌습니다',
            body: '아래 세 가지가 겹쳤습니다. 하나만으로는 위험하다고 하지 않습니다.',
        },
        reasons: [
            {
                kind: 'SIGNAL',
                confidence: 'VERIFIED',
                title: '이탈한 회원들보다도 적게 나오고 있습니다',
                plain:
                    '두 달 전에는 13회를 받았는데 최근 두 달은 6회입니다. 라포에서 실제로 이탈한 회원들이 마지막 두 달에 평균 7회를 받았는데, 이 회원은 그보다도 적습니다.',
                compare: {
                    unit: '회',
                    mine: { label: '이하늘 회원 (최근 60일)', value: 6 },
                    others: [
                        { label: '이탈한 회원들 평균', value: 7, tone: 'BAD' },
                        { label: '재등록한 회원들 평균', value: 11, tone: 'GOOD' },
                    ],
                },
            },
            {
                kind: 'SEGMENT',
                confidence: 'VERIFIED',
                title: '첫 수강권은 원래 가장 많이 빠져나가는 구간입니다',
                plain:
                    '라포 전체에서 첫 수강권 회원 10명 중 7명은 두 번째로 이어지지 않습니다. 두 번째, 세 번째로 갈수록 이탈률이 확 떨어집니다. 이 회원은 지금 가장 위험한 첫 구간에 있습니다.',
                compare: {
                    unit: '%',
                    mine: { label: '1번째 수강권 (이 회원)', value: 68.3 },
                    others: [
                        { label: '2번째 수강권', value: 48.2 },
                        { label: '3번째 수강권', value: 34.4, tone: 'GOOD' },
                    ],
                },
            },
            {
                kind: 'TIMING',
                title: '이야기를 꺼낼 기회가 4번 남았습니다',
                plain:
                    '수강권이 끝나고 나면 연락할 명분이 사라집니다. 남은 수업 안에 재등록 이야기를 꺼내야 하는데, 아직 상담 기록이 없습니다.',
                countdown: [
                    { label: '남은 수업', value: '4회', caption: '이 안에 이야기해야 합니다', alert: true },
                    { label: '만료까지', value: '11일', caption: '2026-08-16', alert: true },
                    { label: '재등록 상담', value: '없음', caption: '기록이 없습니다', alert: true },
                ],
            },
        ],
        timeline: {
            title: '수업 간격이 벌어지고 있습니다',
            from: '2026-06-01',
            to: '2026-08-05',
            dots: [
                { date: '2026-06-04', status: '출석' },
                { date: '2026-06-11', status: '출석' },
                { date: '2026-06-18', status: '출석' },
                { date: '2026-06-25', status: '출석' },
                { date: '2026-07-02', status: '출석' },
                { date: '2026-07-13', status: '출석' },
                { date: '2026-07-27', status: '출석' },
            ],
            note: '6월에는 정확히 일주일 간격이었습니다. 7월 들어 11일 → 14일로 벌어졌고, 마지막 수업 이후로는 9일째 아무 기록이 없습니다.',
        },
        highlights: [
            { label: '수강권 순번', value: '1번째', sub: '라포 전체 이탈률 68.3%', alert: true },
            { label: '소진 페이스', value: '46%', sub: '13회 → 6회', alert: true },
            { label: '잔여', value: '4회', alert: true },
            { label: '만료', value: 'D-11', alert: true },
        ],
        glossary: PACE_GLOSSARY,
        sections: [
            {
                label: '수강권 소진 페이스',
                confidence: 'VERIFIED',
                formula: '최근 60일 유효수업 수 ÷ 이전 60일 유효수업 수 × 100',
                benchmark: '재등록한 회원은 종료 전 60일에 중앙값 11회, 이탈한 회원은 7회를 받았습니다',
                columns: ['구간', '수업 수', '주당 평균'],
                rows: [
                    { cells: ['이전 60일 (4/07~6/05)', '13회', '1.5회'] },
                    { cells: ['최근 60일 (6/06~8/05)', '6회', '0.7회'], alert: true },
                ],
                note: '유효수업 = 취소되지 않은 수업 중 출석 또는 노쇼로 처리된 건',
            },
            {
                label: '최근 수업 이력',
                columns: ['날짜', '시간', '강사', '출결'],
                rows: [
                    { cells: ['2026-07-27', '19:00', '김한솔', '출석'] },
                    { cells: ['2026-07-13', '19:00', '김한솔', '출석'] },
                    { cells: ['2026-07-02', '20:00', '김한솔', '출석'] },
                    { cells: ['2026-06-25', '19:00', '김한솔', '출석'] },
                    { cells: ['2026-06-18', '19:00', '김한솔', '출석'] },
                    { cells: ['2026-06-11', '19:00', '김한솔', '출석'] },
                ],
                note: '마지막 수업 이후 9일 경과. 6월에는 평균 7일 간격이었습니다',
            },
            {
                label: '수강권',
                confidence: 'VERIFIED',
                formula: '잔여 = 총 회차 − 시작 회차 − 취소되지 않은 전체 스케줄 + 1',
                benchmark: '수강권 종료 사유의 98%가 회차 소진입니다. 만료일보다 잔여 회차가 중요합니다',
                columns: ['항목', '값'],
                rows: [
                    { cells: ['총 회차', '20회'] },
                    { cells: ['시작 회차', '1'] },
                    { cells: ['진행한 수업', '16회'] },
                    { cells: ['잔여', '4회'], alert: true },
                    { cells: ['만료일', '2026-08-16 (D-11)'], alert: true },
                    { cells: ['재등록 상담 기록', '없음'], alert: true },
                ],
                note: '미래에 예약된 수업도 차감에 포함됩니다. 서버가 쓰는 공식과 동일합니다',
            },
            {
                label: '수업일지',
                confidence: 'REFERENCE',
                columns: ['날짜', '작성', '회원 열람'],
                rows: [
                    { cells: ['2026-07-27', '작성됨', '열람 안 함'], muted: true },
                    { cells: ['2026-07-20', '작성됨', '열람 안 함'], muted: true },
                    { cells: ['2026-07-13', '작성됨', '열람'], muted: true },
                    { cells: ['2026-07-02', '작성됨', '열람 안 함'], muted: true },
                ],
                note: '일지 열람은 검증에서 이탈 예측력이 확인되지 않았습니다(lift ≈ 0). 판정 근거로 쓰지 않고 상황 이해용으로만 보여드립니다',
            },
        ],
    },

    bi_2: {
        title: '박서준 회원',
        subtitle: '담당 조은오 강사 · PT 15회권 · 2026-04-02 등록 · 첫 수강권',
        verdict: {
            grade: 'DANGER',
            headline: '수업이 사실상 멈췄고 만료가 일주일도 안 남았습니다',
            body: '이 회원은 시간이 가장 급합니다. 강사를 거치면 늦을 수 있습니다.',
        },
        reasons: [
            {
                kind: 'SIGNAL',
                confidence: 'VERIFIED',
                title: '두 달 동안 세 번밖에 나오지 않았습니다',
                plain:
                    '그 앞 두 달에는 13회를 받았습니다. 지금은 이탈한 회원들 평균의 절반도 안 되는 수준이고, 마지막 수업은 19일 전입니다.',
                compare: {
                    unit: '회',
                    mine: { label: '박서준 회원 (최근 60일)', value: 3 },
                    others: [
                        { label: '이탈한 회원들 평균', value: 7, tone: 'BAD' },
                        { label: '재등록한 회원들 평균', value: 11, tone: 'GOOD' },
                    ],
                },
            },
            {
                kind: 'CONTEXT',
                confidence: 'WEAK',
                title: '노쇼가 센터 평균의 세 배입니다',
                plain:
                    '두 달간 노쇼가 2건입니다. 노쇼가 잦은 회원이 더 많이 이탈하는 경향은 있지만, 위에서 본 수업 감소와 따로 떨어진 신호인지는 아직 확인되지 않았습니다. 그래서 이 항목만으로 판단하지는 않았습니다.',
                compare: {
                    unit: '%',
                    mine: { label: '박서준 회원', value: 2.8 },
                    others: [
                        { label: '센터 평균', value: 0.9, tone: 'GOOD' },
                        { label: '라포 전체 평균', value: 1.19, tone: 'GOOD' },
                    ],
                },
            },
            {
                kind: 'TIMING',
                title: '남은 기회가 2번, 6일뿐입니다',
                plain:
                    '이 회원은 강사에게 지시하고 기다릴 시간이 없습니다. 대표님이 직접 연락하시는 편이 확실합니다.',
                countdown: [
                    { label: '남은 수업', value: '2회', alert: true },
                    { label: '만료까지', value: '6일', caption: '2026-08-11', alert: true },
                    { label: '마지막 수업', value: '19일 전', caption: '2026-07-17', alert: true },
                ],
            },
        ],
        timeline: {
            title: '6월 말부터 사실상 멈췄습니다',
            from: '2026-06-01',
            to: '2026-08-05',
            dots: [
                { date: '2026-06-12', status: '취소' },
                { date: '2026-06-24', status: '노쇼' },
                { date: '2026-07-08', status: '노쇼' },
                { date: '2026-07-17', status: '출석' },
            ],
            note: '두 달간 기록이 네 건뿐이고 그중 둘은 노쇼, 하나는 취소입니다. 실제로 수업을 받은 건 7월 17일 한 번이고, 그 이후로는 19일째 비어 있습니다.',
        },
        highlights: [
            { label: '수강권 순번', value: '1번째', sub: '라포 전체 이탈률 68.3%', alert: true },
            { label: '소진 페이스', value: '23%', sub: '13회 → 3회', alert: true },
            { label: '미출석', value: '19일', alert: true },
            { label: '만료', value: 'D-6', alert: true },
        ],
        glossary: PACE_GLOSSARY,
        sections: [
            {
                label: '수강권 소진 페이스',
                confidence: 'VERIFIED',
                formula: '최근 60일 유효수업 수 ÷ 이전 60일 유효수업 수 × 100',
                benchmark: '이탈 회원 중앙값 7회 · 재등록 회원 중앙값 11회',
                columns: ['구간', '수업 수', '주당 평균'],
                rows: [
                    { cells: ['이전 60일', '13회', '1.5회'] },
                    { cells: ['최근 60일', '3회', '0.35회'], alert: true },
                ],
            },
            {
                label: '최근 수업 이력',
                columns: ['날짜', '시간', '강사', '출결'],
                rows: [
                    { cells: ['2026-07-17', '07:00', '조은오', '출석'] },
                    { cells: ['2026-07-08', '07:00', '조은오', '노쇼'], alert: true },
                    { cells: ['2026-06-24', '07:00', '조은오', '노쇼'], alert: true },
                    { cells: ['2026-06-12', '07:00', '조은오', '회원 취소'], muted: true },
                ],
            },
            {
                label: '노쇼 · 취소',
                confidence: 'WEAK',
                formula: '노쇼율 = 노쇼 건수 ÷ 전체 수업 수 × 100',
                benchmark: '센터 평균 0.9% · 라포 전체 평균 1.19%. 이 회원은 2.8%로 약 3배입니다',
                columns: ['날짜', '유형', '사유'],
                rows: [
                    { cells: ['2026-07-08', '노쇼', '-'], alert: true },
                    { cells: ['2026-06-24', '노쇼', '-'], alert: true },
                    { cells: ['2026-06-12', '회원 취소', '출장'] },
                ],
                note: '노쇼율은 검증에서 이탈군이 1.75배 높게 나왔습니다. 다만 소진 페이스와 독립적인지는 아직 확인되지 않아 보조 지표로만 씁니다',
            },
            {
                label: '수강권',
                confidence: 'VERIFIED',
                formula: '잔여 = 총 회차 − 시작 회차 − 취소되지 않은 전체 스케줄 + 1',
                columns: ['항목', '값'],
                rows: [
                    { cells: ['총 회차', '15회'] },
                    { cells: ['잔여', '2회'], alert: true },
                    { cells: ['만료일', '2026-08-11 (D-6)'], alert: true },
                ],
            },
        ],
    },

    bi_3: {
        title: '성현 강사',
        subtitle: '담당 회원 14명 · 7월 수업 182회 (센터 2위) · 첫 수강권 회원 7명',
        verdict: {
            grade: 'WARNING',
            headline: '성과는 상위권인데 약속 관련 지표만 나쁩니다',
            body: '문책이 아니라 부하 조정으로 접근하는 게 맞습니다. 근거를 함께 봐주세요.',
        },
        reasons: [
            {
                kind: 'SIGNAL',
                confidence: 'VERIFIED',
                title: '첫 수강권 회원을 두 번째로 넘기지 못하고 있습니다',
                plain:
                    '센터에서 가장 중요한 지표입니다. 성현 강사는 첫 수강권 회원 7명을 맡고 있는데, 최근 석 달간 전환율이 계속 20% 근처입니다. 센터 평균의 3분의 2 수준이고, 1위 강사와는 세 배 가까이 차이가 납니다.',
                compare: {
                    unit: '%',
                    mine: { label: '성현 강사', value: 21.4 },
                    others: [
                        { label: '센터 평균', value: 31.7 },
                        { label: '김한솔 강사 (센터 1위)', value: 58.3, tone: 'GOOD' },
                    ],
                },
            },
            {
                kind: 'SIGNAL',
                confidence: 'WEAK',
                title: '예약 요청에 답하는 데 오래 걸립니다',
                plain:
                    '회원이 수업을 잡아달라고 요청한 뒤 답을 받기까지 평균 19시간이 걸립니다. 센터 평균의 두 배 가까이입니다. 지금도 31시간째 답을 못 받은 요청이 있습니다.',
                compare: {
                    unit: '시간',
                    mine: { label: '성현 강사', value: 19 },
                    others: [{ label: '센터 평균', value: 10.3, tone: 'GOOD' }],
                },
            },
            {
                kind: 'CONTEXT',
                confidence: 'REFERENCE',
                title: '당일 취소가 많지만 강사 탓으로 단정할 수 없습니다',
                plain:
                    '최근 4주 당일 취소가 11건으로 센터에서 가장 많습니다. 다만 라포는 회원이 직접 당일 취소를 할 수 없는 구조라, 회원이 요청한 취소도 강사가 대신 처리합니다. 즉 이 숫자에는 회원 사정이 섞여 있습니다. 면담에서 확인이 필요한 항목이지 평가 근거는 아닙니다.',
                compare: {
                    unit: '건',
                    mine: { label: '성현 강사 (4주)', value: 11 },
                    others: [{ label: '센터 평균', value: 4.1, tone: 'GOOD' }],
                },
            },
            {
                kind: 'TIMING',
                title: '수업량은 오히려 상위권입니다',
                plain:
                    '182회로 센터 2위이고 노쇼율도 평균보다 낮습니다. 일을 안 하는 게 아니라 일이 너무 많을 가능성이 큽니다. 이 점을 먼저 확인하고 이야기를 시작하세요.',
                countdown: [
                    { label: '7월 수업', value: '182회', caption: '센터 2위 · 평균 98회' },
                    { label: '담당 회원', value: '14명', caption: '센터 평균 11명' },
                    { label: '노쇼율', value: '0.6%', caption: '센터 평균 0.9%' },
                ],
            },
        ],
        highlights: [
            { label: '당일 취소', value: '11건', sub: '이전 4주 3건', alert: true },
            { label: '예약 응답', value: '19시간', sub: '센터 평균 10.3시간', alert: true },
            { label: '1→2차 전환', value: '21.4%', sub: '센터 평균 31.7%', alert: true },
            { label: '담당 첫 수강권', value: '7명', alert: true },
        ],
        glossary: [
            {
                term: '1→2차 전환율',
                desc: '첫 수강권이 끝난 회원 중 두 번째 수강권을 등록한 비율입니다. 첫 수강권 구간의 이탈률이 가장 높기 때문에, 이 숫자가 센터 매출 성장을 좌우합니다.',
            },
            {
                term: '예약 응답 시간',
                desc: '회원이 수업 예약을 요청한 순간부터 강사가 승인하거나 거절할 때까지 걸린 시간입니다.',
            },
            {
                term: '당일 취소',
                desc: '수업 당일에 취소된 건입니다. 라포에서는 회원이 직접 당일 취소를 할 수 없어 강사가 대신 처리하므로, 회원 요청분이 섞여 있습니다.',
            },
        ],
        sections: [
            {
                label: '당일 취소 내역 (최근 4주)',
                confidence: 'REFERENCE',
                benchmark: '센터 평균 4.1건. 다만 아래 주의사항을 함께 봐주세요',
                columns: ['날짜', '회원', '입력된 사유'],
                rows: [
                    { cells: ['2026-08-04', '최지은', '수업일정 착오로 인해 취소'] },
                    { cells: ['2026-08-01', 'Jason', '개인사정'] },
                    { cells: ['2026-07-30', '강도경', '.'], muted: true },
                    { cells: ['2026-07-28', '한수경', '개인사유'] },
                    { cells: ['2026-07-25', '장윤문', '.'], muted: true },
                ],
                note: '⚠️ 라포는 회원이 당일 취소를 할 수 없는 구조라, 회원이 요청한 취소도 강사가 대신 처리합니다. 즉 이 건수 전부가 강사 귀책은 아닙니다. 사유가 "."인 건은 확인이 어렵습니다. 면담에서 확인해 주세요',
            },
            {
                label: '예약 요청 응답',
                confidence: 'WEAK',
                formula: '응답 시간 = 요청 생성 시각 → 승인·거절 처리 시각',
                benchmark: '라포 전체 평균 — 승인 10.3시간 · 거절 23.6시간',
                columns: ['요청일', '회원', '상태', '응답까지'],
                rows: [
                    { cells: ['2026-08-04', '김나영', '대기', '18시간 경과'], alert: true },
                    { cells: ['2026-08-03', '한수경', '대기', '31시간 경과'], alert: true },
                    { cells: ['2026-08-01', '최지은', '승인', '22시간'] },
                    { cells: ['2026-07-30', '강도경', '승인', '9시간'] },
                ],
                note: '대기 상태로 방치된 요청은 회원이 다른 시간을 찾게 만듭니다',
            },
            {
                label: '첫 수강권 회원 전환',
                confidence: 'VERIFIED',
                formula: '전환율 = 첫 수강권 종료 후 두 번째를 등록한 회원 ÷ 종료한 첫 수강권 회원',
                benchmark: '센터 평균 31.7% · 센터 1위 김한솔 강사 58.3%',
                columns: ['월', '종료', '전환', '전환율'],
                rows: [
                    { cells: ['2026-07', '5명', '1명', '20.0%'], alert: true },
                    { cells: ['2026-06', '4명', '1명', '25.0%'], alert: true },
                    { cells: ['2026-05', '5명', '1명', '20.0%'], alert: true },
                ],
                note: '이 지표가 강사 평가에서 가장 중요합니다. 첫 수강권 회원을 넘기지 못하면 센터 매출이 늘지 않습니다',
            },
            {
                label: '성과 지표',
                columns: ['지표', '성현', '센터 평균'],
                rows: [
                    { cells: ['7월 수업', '182회', '98회'] },
                    { cells: ['담당 회원', '14명', '11명'] },
                    { cells: ['노쇼율', '0.6%', '0.9%'] },
                    { cells: ['일지 작성률', '72%', '48%'] },
                ],
                note: '성과 자체는 상위권입니다. 문책보다 부하 조정으로 접근하는 게 맞습니다',
            },
        ],
    },

    bi_4: {
        title: '최민서 회원',
        subtitle: '담당 김지원 강사 · PT 30회권 · 2026-02-08 등록 · 3번째 수강권',
        verdict: {
            grade: 'CHANCE',
            headline: '가장 확실한 재등록 후보입니다',
            body: '위험해서 올라온 게 아니라, 지금이 이야기를 꺼낼 시점이라 올라왔습니다.',
        },
        reasons: [
            {
                kind: 'SEGMENT',
                confidence: 'VERIFIED',
                title: '세 번째 수강권까지 이어온 회원입니다',
                plain:
                    '10회 → 20회 → 30회로 회차를 늘려가며 이어왔습니다. 세 번째 수강권 구간은 이탈률이 34.4%까지 떨어집니다. 첫 수강권 회원의 절반 수준입니다.',
                compare: {
                    unit: '%',
                    mine: { label: '3번째 수강권 (이 회원)', value: 34.4 },
                    others: [
                        { label: '2번째 수강권', value: 48.2 },
                        { label: '1번째 수강권', value: 68.3, tone: 'BAD' },
                    ],
                },
            },
            {
                kind: 'SIGNAL',
                confidence: 'VERIFIED',
                title: '수업 속도가 오히려 빨라졌습니다',
                plain:
                    '보통 수강권이 끝나갈수록 속도가 떨어지는데, 이 회원은 반대입니다. 재등록한 회원들 평균보다도 많이 나오고 있고 노쇼는 한 번도 없습니다.',
                compare: {
                    unit: '회',
                    mine: { label: '최민서 회원 (최근 60일)', value: 14 },
                    others: [
                        { label: '재등록한 회원들 평균', value: 11, tone: 'GOOD' },
                        { label: '이탈한 회원들 평균', value: 7, tone: 'BAD' },
                    ],
                },
            },
            {
                kind: 'TIMING',
                title: '잔여 3회 — 지금이 상담 시점입니다',
                plain:
                    '재등록 이야기는 너무 이르면 부담스럽고 너무 늦으면 기회를 놓칩니다. 잔여 3회는 그 사이 구간입니다. 지금 꺼내면 성공률이 가장 높습니다.',
                countdown: [
                    { label: '남은 수업', value: '3회', caption: '상담 적기' },
                    { label: '만료까지', value: '18일' },
                    { label: '노쇼', value: '0회', caption: '한 번도 없습니다' },
                ],
            },
        ],
        timeline: {
            title: '6개월간 흔들림이 없습니다',
            from: '2026-06-01',
            to: '2026-08-05',
            dots: [
                { date: '2026-06-03', status: '출석' },
                { date: '2026-06-11', status: '출석' },
                { date: '2026-06-15', status: '출석' },
                { date: '2026-06-19', status: '출석' },
                { date: '2026-06-24', status: '출석' },
                { date: '2026-06-28', status: '출석' },
                { date: '2026-07-03', status: '출석' },
                { date: '2026-07-08', status: '출석' },
                { date: '2026-07-14', status: '출석' },
                { date: '2026-07-19', status: '출석' },
                { date: '2026-07-24', status: '출석' },
                { date: '2026-07-28', status: '출석' },
                { date: '2026-07-30', status: '출석' },
                { date: '2026-08-01', status: '출석' },
                { date: '2026-08-04', status: '출석' },
            ],
            note: '평균 2~3일 간격으로 촘촘합니다. 빈 구간이 없다는 것이 이 회원의 가장 강한 신호입니다.',
        },
        highlights: [
            { label: '수강권 순번', value: '3번째', sub: '라포 전체 이탈률 34.4%' },
            { label: '소진 페이스', value: '108%', sub: '13회 → 14회' },
            { label: '잔여', value: '3회', alert: true },
            { label: '노쇼', value: '0회' },
        ],
        glossary: PACE_GLOSSARY,
        sections: [
            {
                label: '수강권 이력',
                confidence: 'VERIFIED',
                benchmark: '순번이 올라갈수록 이탈률이 떨어집니다 — 1번째 68.3% → 2번째 48.2% → 3번째 34.4%',
                columns: ['순번', '상품', '기간', '결과'],
                rows: [
                    { cells: ['1번째', 'PT 10회', '2026-02 ~ 2026-04', '재등록'] },
                    { cells: ['2번째', 'PT 20회', '2026-04 ~ 2026-06', '재등록'] },
                    { cells: ['3번째', 'PT 30회', '2026-06 ~ 진행 중', '잔여 3회'], alert: true },
                ],
                note: '회차를 늘려가며 이어온 회원입니다. 만족도가 높다는 신호입니다',
            },
            {
                label: '최근 수업 이력',
                columns: ['날짜', '시간', '강사', '출결'],
                rows: [
                    { cells: ['2026-08-04', '10:00', '김지원', '출석'] },
                    { cells: ['2026-08-01', '10:00', '김지원', '출석'] },
                    { cells: ['2026-07-30', '10:00', '김지원', '출석'] },
                    { cells: ['2026-07-28', '10:00', '김지원', '출석'] },
                ],
                note: '평균 2~3일 간격으로 꾸준합니다',
            },
        ],
    },

    bi_5: {
        title: '김한솔 강사',
        subtitle: '담당 회원 12명 · 7월 수업 140회 · 첫 수강권 회원 5명',
        verdict: {
            grade: 'PRAISE',
            headline: '전환율과 일지 작성률 모두 센터 1위입니다',
            body: '잘하고 있다는 말보다, 무엇을 다르게 하는지 물어볼 근거를 함께 드립니다.',
        },
        reasons: [
            {
                kind: 'SIGNAL',
                confidence: 'VERIFIED',
                title: '가장 어려운 구간에서 가장 좋은 성과를 내고 있습니다',
                plain:
                    '첫 수강권 회원을 두 번째로 넘기는 비율이 58.3%입니다. 센터 평균의 두 배 가까이이고, 라포 전체 기준으로 보면 첫 수강권 회원 10명 중 7명이 떠나는 구간에서 절반 이상을 잡아내고 있다는 뜻입니다.',
                compare: {
                    unit: '%',
                    mine: { label: '김한솔 강사', value: 58.3 },
                    others: [
                        { label: '센터 평균', value: 31.7 },
                        { label: '성현 강사', value: 21.4, tone: 'BAD' },
                    ],
                },
            },
            {
                kind: 'SIGNAL',
                confidence: 'WEAK',
                title: '예약 요청에 가장 빨리 답합니다',
                plain:
                    '회원이 수업을 요청하면 평균 4시간 안에 답이 갑니다. 센터 평균의 절반 이하입니다. 회원 입장에서는 "말하면 바로 잡힌다"는 경험이 쌓입니다.',
                compare: {
                    unit: '시간',
                    mine: { label: '김한솔 강사', value: 4 },
                    others: [
                        { label: '센터 평균', value: 10.3, tone: 'BAD' },
                        { label: '성현 강사', value: 19, tone: 'BAD' },
                    ],
                },
            },
            {
                kind: 'CONTEXT',
                confidence: 'REFERENCE',
                title: '일지를 140회 전부 작성했습니다',
                plain:
                    '일지 작성 자체가 이탈을 막는다는 근거는 검증에서 확인되지 않았습니다. 그래서 성과의 원인이라고 말하지는 않겠습니다. 다만 성실도를 보여주는 기록이고, 칭찬할 때 구체적으로 짚을 수 있는 지점입니다.',
                compare: {
                    unit: '%',
                    mine: { label: '김한솔 강사', value: 100 },
                    others: [{ label: '센터 평균', value: 48, tone: 'BAD' }],
                },
            },
            {
                kind: 'TIMING',
                title: '지금 물어보면 다른 강사에게 옮길 수 있습니다',
                plain:
                    '성과가 좋은 이유를 본인은 당연하게 여겨서 먼저 말하지 않습니다. 성현 강사의 전환율이 21.4%인 지금이 물어볼 시점입니다.',
                countdown: [
                    { label: '담당 회원 이탈', value: '0명', caption: '최근 3개월' },
                    { label: '담당 회원', value: '12명', caption: '첫 수강권 5명' },
                    { label: '당일 취소', value: '2건', caption: '센터 평균 4.1건' },
                ],
            },
        ],
        highlights: [
            { label: '1→2차 전환', value: '58.3%', sub: '센터 평균 31.7%' },
            { label: '일지 작성률', value: '100%', sub: '센터 평균 48%' },
            { label: '담당 회원 이탈', value: '0명', sub: '최근 3개월' },
            { label: '예약 응답', value: '4시간', sub: '센터 평균 10.3시간' },
        ],
        glossary: [
            {
                term: '1→2차 전환율',
                desc: '첫 수강권이 끝난 회원 중 두 번째 수강권을 등록한 비율입니다. 첫 수강권 구간의 이탈률이 가장 높기 때문에, 이 숫자가 센터 매출 성장을 좌우합니다.',
            },
            {
                term: '일지 작성률',
                desc: '진행한 수업 중 강사가 수업일지를 남긴 비율입니다.',
            },
        ],
        sections: [
            {
                label: '첫 수강권 회원 전환',
                confidence: 'VERIFIED',
                formula: '전환율 = 첫 수강권 종료 후 두 번째를 등록한 회원 ÷ 종료한 첫 수강권 회원',
                benchmark: '센터 평균 31.7% · 라포 전체 첫 수강권 이탈률 68.3%',
                columns: ['월', '종료', '전환', '전환율'],
                rows: [
                    { cells: ['2026-07', '4명', '3명', '75.0%'] },
                    { cells: ['2026-06', '3명', '2명', '66.7%'] },
                    { cells: ['2026-05', '5명', '2명', '40.0%'] },
                ],
                note: '가장 어려운 구간에서 가장 좋은 성과를 내고 있습니다',
            },
            {
                label: '수업일지 작성',
                confidence: 'REFERENCE',
                benchmark: '센터 평균 48% · 라포 전체 평균 48.4%',
                columns: ['월', '대상 수업', '작성', '작성률'],
                rows: [
                    { cells: ['2026-07', '140회', '140회', '100%'] },
                    { cells: ['2026-06', '132회', '132회', '100%'] },
                    { cells: ['2026-05', '128회', '124회', '97%'] },
                    { cells: ['2026-04', '119회', '113회', '95%'] },
                ],
                note: '일지 작성 자체가 이탈을 막는다는 근거는 아직 없습니다. 다만 성실도를 보여주는 지표입니다',
            },
            {
                label: '담당 회원 현황',
                columns: ['지표', '값'],
                rows: [
                    { cells: ['담당 회원', '12명'] },
                    { cells: ['첫 수강권 회원', '5명'] },
                    { cells: ['최근 3개월 이탈', '0명'] },
                    { cells: ['당일 취소', '2건'] },
                ],
            },
        ],
    },
};
